/**
 * A2uiSurfaceCard — the web SESSION surface's renderer for an `a2ui_surface`
 * card (DEC-3). It is the a2ui twin of `SessionBlockCard`: it consumes the card's
 * append-only `envelopes[]` log (a STANDARD a2ui 1.0 message stream), folds it
 * into a surface + reactive data model, and renders the surface's `root` tree by
 * reusing the EXISTING viewIr session-block kit as a smart wrapper.
 *
 * Contract, identical to SessionBlockCard so the two mount the same way:
 *   props { card, onCardAction, onFollowUp, onA2uiAction }
 *   - core-18 nodes (Row/Column/Panel/Card/Text/Image/Video/Button) render here
 *   - the 14 session customs render through `ViewIRComposition` (the same kit)
 *   - Button / chip taps post a v1 `follow_up` action (the standard return
 *     channel) via `onA2uiAction`, falling back to the composer `onFollowUp`
 *   - an unknown component draws a compact chip in DEV, nothing in prod, and
 *     NEVER blanks its siblings.
 *
 * In-place refinement is a store concern: a later turn's envelopes arrive on the
 * SAME card id (the reducer keys the card by surfaceId), so this component simply
 * re-folds the longer log and re-renders — no duplicate card, no remount here.
 */
import { useCallback, useMemo, useState } from 'react';
import Markdown from 'react-markdown';
import { ViewIRComposition } from '../widget-native/viewir/interpreter';
import KitKeyframes from '../widget-native/KitKeyframes';
import { lookupKit } from '../widget-native/viewir/registry';
import { buildSessionTokenStyle } from '../widget-native/viewir/tokens';
import { surfaceFromCard, resolveNode } from '../../a2ui/sessionProcessor';
import { SESSION_CUSTOM_COMPONENTS } from '../../a2ui/catalog';
import { setPointer } from '../../a2ui/core';
import {
  makeSessionEmit, customToBlockNode, actionIntent, buildFollowUpEnvelope,
} from '../../a2ui/componentApis';
import {
  ViIcon, ViDivider, ViAudioPlayer, ViCheckBox, ViSlider, ViTextField, ViChoicePicker, ViDateTimeInput,
} from '../../a2ui/coreControls';
import { recognisedCollovExtension } from '../../a2ui/collovExtensionMatch';
import { CollovExtensionHost } from '../../a2ui/collovExtensions';

// Markdown element styling for the surface's core `Text` (headings + bold render
// as real headings/bold, not flat body text). The app WebView resets heading
// styles, so we set them explicitly.
const MD_COMPONENTS = {
  h1: ({ node, ...p }) => <h1 style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.3, margin: '0.4em 0 0.3em' }} {...p} />,
  h2: ({ node, ...p }) => <h2 style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.3, margin: '0.4em 0 0.3em' }} {...p} />,
  h3: ({ node, ...p }) => <h3 style={{ fontSize: 16, fontWeight: 700, lineHeight: 1.3, margin: '0.4em 0 0.3em' }} {...p} />,
  strong: ({ node, ...p }) => <strong style={{ fontWeight: 700 }} {...p} />,
  p: ({ node, ...p }) => <p style={{ margin: '0.3em 0' }} {...p} />,
};

// Text `variant` → our type scale (core-superset: adds h1/h2/h3 to caption/body).
// The runner authors headings as Text{variant:"h2"} now, not `## ` markdown.
const TEXT_VARIANT_STYLE = {
  h1: { fontSize: 22, fontWeight: 700, lineHeight: 1.25, margin: '2px 0' },
  h2: { fontSize: 18, fontWeight: 700, lineHeight: 1.3, margin: '2px 0' },
  h3: { fontSize: 16, fontWeight: 700, lineHeight: 1.3, margin: '2px 0' },
  body: { fontSize: 15, lineHeight: 1.5 },
  caption: { fontSize: 12.5, lineHeight: 1.4, color: 'var(--vi-text-muted, #8E8E93)' },
};

// The pending-media placeholder our customs drew — a shimmer plate at the media
// aspect until the data model fills the bound url, then the image paints.
function MediaSkeleton({ ratio = '4/3' }) {
  return (
    <div data-a2ui-media-pending style={{
      width: '100%', aspectRatio: ratio, borderRadius: 12, overflow: 'hidden',
      background: 'linear-gradient(90deg, var(--vi-surface-muted,#EFEFF4) 25%, var(--vi-hair,#E5E5EA) 37%, var(--vi-surface-muted,#EFEFF4) 63%)',
      backgroundSize: '400% 100%', animation: 'viNativeShimmer 1.4s ease infinite',
    }} />
  );
}

function ImageViewer({ src, onClose }) {
  if (!src) return null;
  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(0,0,0,0.92)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <img src={src} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
    </div>
  );
}

export default function A2uiSurfaceCard({ card, onCardAction, onFollowUp, onA2uiAction }) {
  const [viewing, setViewing] = useState(null);
  // local data-model overlay: a user's edits to interactive components (DEC-8).
  // Writes land here; the tree re-resolves off the merged model so bound
  // siblings + a submit Button's action context see the edit.
  const [overlay, setOverlay] = useState({});
  const style = useMemo(() => buildSessionTokenStyle({ appearance: 'light' }), []);
  const cardId = card?.cardId || card?.id;

  const surface = useMemo(() => surfaceFromCard(card), [card]);
  const surfaceId = card?.data?.surfaceId || surface?.surfaceId;

  const mergedSurface = useMemo(() => {
    if (!surface || !Object.keys(overlay).length) return surface;
    let dm = structuredClone(surface.dataModel || {});
    for (const [p, v] of Object.entries(overlay)) {
      try { dm = setPointer(dm, p, v); } catch { /* ignore a bad path */ }
    }
    return { ...surface, dataModel: dm };
  }, [surface, overlay]);

  const writeData = useCallback((path, value) => {
    if (path) setOverlay((o) => ({ ...o, [path]: value }));
  }, []);

  const emitFollowUp = useCallback((userMessage, sourceComponentId, context = {}) => {
    const words = typeof userMessage === 'string' ? userMessage.trim() : '';
    if (!words) return;
    if (onA2uiAction) {
      onA2uiAction(buildFollowUpEnvelope({ surfaceId, sourceComponentId, userMessage: words, context }));
    } else {
      onFollowUp?.(words);
    }
  }, [onA2uiAction, onFollowUp, surfaceId]);

  const tree = useMemo(() => {
    if (!mergedSurface) return null;
    return resolveNode(mergedSurface, 'root');
  }, [mergedSurface]);

  if (!surface || !tree) return null;

  return (
    <div style={{ ...style, padding: 0 }} data-a2ui-surface={surfaceId || 'surface'}>
      <KitKeyframes />
      <A2uiNode
        node={tree}
        cardId={cardId}
        onCardAction={onCardAction}
        emitFollowUp={emitFollowUp}
        onOpenImage={setViewing}
        writeData={writeData}
      />
      <ImageViewer src={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}

const WORD_CHAR = /[\p{L}\p{N}]/u;

function textOf(node) {
  if (!node || node.component !== 'Text') return null;
  const t = node.props?.text != null ? node.props.text : node.props?.value;
  return typeof t === 'string' ? t.trim() : null;
}

const plainText = (md) => md.replace(/\*\*|__|^#+\s*/g, '').trim();

// A core Icon, or a Text holding only an emoji/symbol: drawn as a fixed tile, never as a flex cell.
function iconMarkOf(node) {
  if (!node) return null;
  if (node.component === 'Icon') return { name: node.props?.name };
  const t = textOf(node);
  if (!t || [...t].length > 8 || WORD_CHAR.test(t)) return null;
  return { emoji: t };
}

// Row[mark, Column[Text…]] or Row[mark, Text…] → { mark, title, body[] }; anything else → null.
function iconRowParts(node) {
  if (!node || node.component !== 'Row') return null;
  const kids = node.children || [];
  if (kids.length < 2) return null;
  const mark = iconMarkOf(kids[0]);
  if (!mark) return null;
  let rest = kids.slice(1);
  if (rest.length === 1 && (rest[0]?.component === 'Column'
    || (rest[0]?.component === 'List' && rest[0]?.props?.direction !== 'horizontal'))) {
    rest = rest[0].children || [];
  }
  const texts = [];
  for (const n of rest) {
    const t = textOf(n);
    if (t == null) return null;
    if (t) texts.push(plainText(t));
  }
  if (!texts.length) return null;
  return { mark, title: texts[0], body: texts.slice(1) };
}

function IconTile({ mark }) {
  return (
    <span data-a2ui-icon-tile="" style={{
      flex: '0 0 32px', width: 32, height: 32, borderRadius: 8, background: 'var(--surface-3)',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, lineHeight: 1,
      color: 'var(--text-2)',
    }}>
      {mark.emoji != null ? mark.emoji : <ViIcon name={mark.name} size={16} />}
    </span>
  );
}

const CLAMP = (lines) => ({ display: '-webkit-box', WebkitLineClamp: lines, WebkitBoxOrient: 'vertical', overflow: 'hidden' });

function IconRow({ parts, framed }) {
  return (
    <div data-a2ui-icon-row={framed ? 'card' : 'row'} style={{
      display: 'flex', alignItems: 'flex-start', gap: 12, boxSizing: 'border-box', width: '100%', minWidth: 0,
      ...(framed
        ? { padding: 14, borderRadius: 16, background: 'var(--surface)', boxShadow: 'var(--shadow-1)' }
        : { padding: '6px 0' }),
    }}>
      <IconTile mark={parts.mark} />
      <div data-a2ui-icon-row-text="" style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.35, color: 'var(--text)', overflowWrap: 'anywhere', ...CLAMP(2) }}>
          {parts.title}
        </span>
        {parts.body.map((line, i) => (
          <span key={i} style={{ fontSize: 14, lineHeight: 1.4, color: 'var(--text-2)', overflowWrap: 'anywhere', ...CLAMP(3) }}>
            {line}
          </span>
        ))}
      </div>
    </div>
  );
}

function A2uiNode({ node, cardId, onCardAction, emitFollowUp, onOpenImage, writeData }) {
  if (!node || node.visible === false) return null;
  const { component, props = {}, children } = node;

  // A host (List/Row/Column) carrying a recognised `metadata.extensions.collov_*`
  // renders OUR rich layout; the relayed envelope also carries the expanded core
  // children (which is what a third-party renderer draws). An unrecognised or
  // empty extension falls through to the plain core children below.
  const collov = recognisedCollovExtension(props.metadata);
  if (collov) {
    return (
      <CollovExtensionHost
        extKey={collov.key}
        payload={collov.payload}
        onOpenImage={onOpenImage}
        onOpenUrl={(url) => { if (url) onCardAction?.({ action: 'open_url', cardId, url }); }}
      />
    );
  }

  const renderOne = (n, key) => (n && typeof n === 'object' && n.component ? (
    <A2uiNode key={key} node={n} cardId={cardId} onCardAction={onCardAction} emitFollowUp={emitFollowUp} onOpenImage={onOpenImage} writeData={writeData} />
  ) : null);

  const renderChildren = (dir) => (
    <div
      style={{
        display: 'flex', flexDirection: dir,
        gap: typeof props.gap === 'number' ? props.gap : gapFor(props.gap),
        alignItems: props.align === 'center' ? 'center' : props.align === 'end' ? 'flex-end' : 'stretch',
        justifyContent: justifyFor(props.justify),
        flexWrap: props.wrap ? 'wrap' : 'nowrap',
      }}
    >
      {(children || []).map((c, i) => {
        const mark = dir === 'row' ? iconMarkOf(c) : null;
        return mark ? <IconTile key={c.key || c.id || i} mark={mark} /> : renderOne(c, c.key || c.id || i);
      })}
    </div>
  );

  if (component === 'Row') {
    const parts = iconRowParts(node);
    if (parts) return <IconRow parts={parts} framed={false} />;
    return renderChildren('row');
  }
  // a2ui core List = a directional container (default vertical)
  if (component === 'Column') return renderChildren('column');
  if (component === 'List') return renderChildren(props.direction === 'horizontal' ? 'row' : 'column');

  // ── core-18 leaves + interactive controls ──────────────────────────────────
  if (component === 'Icon') return <ViIcon name={props.name} size={props.size} tone={props.tone} />;
  if (component === 'Divider') return <ViDivider axis={props.axis} />;
  if (component === 'AudioPlayer') return <ViAudioPlayer url={props.url} description={props.description} />;
  if (component === 'CheckBox') {
    return <ViCheckBox label={props.label} checked={props.value} onChange={(v) => writeData?.(props._valuePath, v)} />;
  }
  if (component === 'Slider') {
    return <ViSlider label={props.label} value={props.value} min={props.min} max={props.max} step={props.steps}
      onChange={(v) => writeData?.(props._valuePath, v)} />;
  }
  if (component === 'TextField') {
    return <ViTextField label={props.label} value={props.value} placeholder={props.placeholder} variant={props.variant}
      onChange={(v) => writeData?.(props._valuePath, v)} />;
  }
  if (component === 'ChoicePicker') {
    return <ViChoicePicker label={props.label} options={props.options} value={props.value}
      multiple={props.variant === 'multipleSelection'} displayStyle={props.displayStyle}
      onChange={(v) => writeData?.(props._valuePath, v)} />;
  }
  if (component === 'DateTimeInput') {
    return <ViDateTimeInput label={props.label} value={props.value} enableDate={props.enableDate} enableTime={props.enableTime}
      onChange={(v) => writeData?.(props._valuePath, v)} />;
  }
  if (component === 'Card' && !(children && children.length)) {
    const parts = iconRowParts(props.child);
    if (parts) return <IconRow parts={parts} framed />;
  }
  if (component === 'Panel' || component === 'Card') {
    // a2ui core Card carries a SINGLE `child` (a ComponentId ref); Panel carries a
    // ChildList. Card gets our card look (a grid cell); Panel is a plain group.
    const cardStyle = component === 'Card'
      ? {
        display: 'flex', flexDirection: 'column', gap: 6, padding: 12, borderRadius: 14,
        background: 'var(--vi-surface, #fff)', border: '1px solid var(--vi-hair, #ECECEC)',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
      }
      : { display: 'block' };
    return (
      <div style={cardStyle} data-a2ui-card={component === 'Card' ? '1' : undefined}>
        {renderOne(props.child, 'child')}
        {children && children.length ? renderChildren('column') : null}
      </div>
    );
  }
  if (component === 'Modal') {
    return (
      <div data-a2ui-modal>
        {renderOne(props.trigger, 'trigger')}
        {renderOne(props.content, 'content')}
      </div>
    );
  }
  if (component === 'Tabs') {
    return (
      <div data-a2ui-tabs>
        {(props.tabs || []).map((t, i) => (
          <div key={i}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{t.title}</div>
            {renderOne(t.child, `tab${i}`)}
          </div>
        ))}
      </div>
    );
  }

  if (component === 'Text') {
    const text = props.text != null ? props.text : props.value;
    if (text == null || text === '') return null;
    // a2ui core Text supports Markdown AND a `variant` (h1/h2/h3/body/caption) —
    // the runner authors headings as `variant`, inline emphasis as `**bold**`.
    const vstyle = TEXT_VARIANT_STYLE[props.variant] || TEXT_VARIANT_STYLE.body;
    return (
      <div style={vstyle} data-a2ui-text data-variant={props.variant || 'body'}>
        <Markdown components={MD_COMPONENTS}>{String(text)}</Markdown>
      </div>
    );
  }

  if (component === 'Image') {
    const url = props.url || props.src;
    if (!url) {
      // bound to a still-pending /media/<slot>/url → our skeleton, painted on fill
      return props._mediaBound ? <MediaSkeleton ratio={props.ratio} /> : null;
    }
    return (
      <figure style={{ margin: 0 }}>
        <img
          src={url}
          alt={props.description || props.alt || ''}
          data-a2ui-image
          onClick={() => onOpenImage?.(url)}
          style={{ width: '100%', borderRadius: 12, cursor: 'zoom-in', objectFit: 'cover', aspectRatio: props.ratio || undefined }}
        />
      </figure>
    );
  }

  if (component === 'Video') {
    const url = props.url || props.src;
    const poster = props.posterUrl || props.poster;
    if (!url) return props._mediaBound ? <MediaSkeleton ratio={props.ratio || '16/9'} /> : null;
    return (
      <video data-a2ui-video controls src={url} poster={poster}
        style={{ width: '100%', borderRadius: 12, aspectRatio: props.ratio || '16/9', background: '#000' }}>
        <track kind="captions" />
      </video>
    );
  }

  if (component === 'Button') {
    const intent = actionIntent(props.action);
    const label = props.label || textOfChild(props) || (intent && intent.userMessage) || 'Continue';
    return (
      <button
        type="button"
        data-a2ui-button
        onClick={() => intent && emitFollowUp(intent.userMessage || label, node.id, intent.context)}
        style={{
          appearance: 'none', border: '1px solid var(--vi-hair, #E5E5EA)', borderRadius: 999,
          padding: '8px 16px', background: 'var(--vi-surface, #fff)', font: 'inherit', cursor: 'pointer',
        }}
      >
        {label}
      </button>
    );
  }

  // the 14 session customs (+ anything the viewIr kit knows) → the existing kit
  if (SESSION_CUSTOM_COMPONENTS.has(component) || lookupKit(component)) {
    return renderCustom(customToBlockNode(node), cardId, onCardAction, emitFollowUp, onOpenImage, component);
  }

  // unknown component: keep siblings; a chip in DEV, nothing in prod
  if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV) {
    return (
      <span
        data-a2ui-unsupported={component}
        style={{
          display: 'inline-block', fontSize: 11, padding: '2px 8px', borderRadius: 8,
          background: '#FFF3CD', color: '#7A5B00', border: '1px solid #FFE69C',
        }}
      >
        unsupported: {component}
      </span>
    );
  }
  return null;
}

function renderCustom(blockNode, cardId, onCardAction, emitFollowUp, onOpenImage, type) {
  const emit = makeSessionEmit({
    type: type || blockNode.type,
    cardId,
    onCardAction,
    onFollowUp: (t) => emitFollowUp(t, cardId),
    onOpenImage,
  });
  return (
    <ViewIRComposition
      type={blockNode.type}
      nodeId={`a2ui-${cardId || blockNode.type}-${blockNode.type}`}
      def={{ composition: blockNode }}
      props={{}}
      ctx={{}}
      emit={emit}
    />
  );
}

function textOfChild(props) {
  const c = props.child;
  if (c && typeof c === 'object') {
    // a resolved child node ({ component, props }) or an inline { text/value/label }
    const p = c.props && typeof c.props === 'object' ? c.props : c;
    return p.text || p.value || p.label;
  }
  return typeof c === 'string' ? c : undefined;
}
function gapFor(g) {
  if (g === 'sm') return 6; if (g === 'lg') return 20; if (g === 'md') return 12;
  return 12;
}
function justifyFor(j) {
  if (j === 'center') return 'center';
  if (j === 'end') return 'flex-end';
  if (j === 'between') return 'space-between';
  return 'flex-start';
}
